import React, {useEffect, useState} from 'react';
import {IntlShape, FormattedMessage} from 'react-intl';
import moment from "moment-timezone";
import _ from 'lodash'
import {PaperSubmission} from '../types/types';

interface CallForPapersMailtoLinkProps {
    intlId: string; // Die ID des Lokalisierungsstrings
    intl: IntlShape;
    submission?: PaperSubmission;
}

/**
 * Gibt den Text-Absatz mit dem Hinweis auf die abgelaufene Bearbeitungsfrist
 * einer Einreichung mit einem Link zur Mitteilung von Änderungswünschen per
 * E-Mail aus.
 *
 * @constructor
 */
const CallForPapersMailtoLink: React.FC<CallForPapersMailtoLinkProps> = (
    {
        intlId,
        intl,
        submission
    }
) => {
    const [mailtoLink, setMailtoLink] = useState('');

    // Generiert den mailto-Link
    useEffect(() => {

        // E-Mail-Empfänger
        const recipient = submission?.callForPapers.emailReplyToAddress
            || submission?.callForPapers.emailBlindCopyAddress
            || submission?.callForPapers.emailFromAddress
            || process.env.REACT_APP_PCO_DEFAULT_MAILTO_ADDRESS;

        if (!submission || !recipient) {
            return;
        }

        // Laut Spezifikation müssen Zeilenumbrüche im Mail-Body mit %0D%0A, also
        // einem Carriage Return und einem LineFeed, encoded werden. Siehe auch:
        // https://www.rfc-editor.org/rfc/rfc6068#section-5
        const crlf = '\r\n';
        const reference = submission.eventNumber || submission.id;

        // Betreff-Zeile
        const subject = encodeURIComponent(intl.formatMessage(
            {id: 'submission.mailto.subject'},
            {reference: reference, title: submission.title}
        ));

        // Es werden zunächst alle Absätze des Mail-Bodys einzeln aufgebaut, da einige
        // von ihnen optional sind und diese dann inklusive der einem Absatz folgenden
        // Zeilenumbrüche ausglassen werden können. Des Weiteren können wir keinen Multi-
        // line-String verwenden, da Zeilenumbrüche mit CRLF notiert werden müssen. Vgl.
        // https://www.rfc-editor.org/rfc/rfc6068#section-5

        // Begrüßung und Einleitung
        const preamble = crlf +
            intl.formatMessage({id: 'submission.mailto.salutation'}) + crlf + crlf +
            intl.formatMessage(
                {id: 'submission.mailto.introduction'},
                {reference: reference}
            ) + crlf;

        // Grußwort
        const farewell = crlf +
            intl.formatMessage({id: 'submission.mailto.farewell'}) + crlf + crlf +
            submission.submittedBy.mainContact.firstName + ' ' +
            submission.submittedBy.mainContact.lastName + crlf;

        // Änderungswünsche zum Titel der Einreichung
        const title = crlf +
            intl.formatMessage({id: 'submission.mailto.label.title'}) + crlf +
            intl.formatMessage({id: 'submission.mailto.label.todate'}) +
            (submission.title || '–') + crlf +
            intl.formatMessage({id: 'submission.mailto.label.new'}) + crlf;

        // Änderungswünsche zum Subtitel der Einreichung
        const subtitle = crlf +
            intl.formatMessage({id: 'submission.mailto.label.subtitle'}) + crlf +
            intl.formatMessage({id: 'submission.mailto.label.todate'}) +
            (submission.subTitle || '–') + crlf +
            intl.formatMessage({id: 'submission.mailto.label.new'}) + crlf;

        // Änderungswünsche zum Abstract der Einreichung
        const abstract = crlf +
            intl.formatMessage({id: 'submission.mailto.label.abstract'}) + crlf +
            intl.formatMessage({id: 'submission.mailto.label.todate'}) +
            (submission.abstract || '–') + crlf +
            intl.formatMessage({id: 'submission.mailto.label.new'}) + crlf;

        // Sonstige Änderungswünsche
        const miscellaneous = crlf +
            intl.formatMessage({id: 'submission.mailto.label.miscellaneous'}) + crlf +
            intl.formatMessage({id: 'submission.mailto.label.new'}) + crlf;

        // Änderungswünsche zum Wunschzeitraum der Einreichung
        const requestedDateTimePeriod = (): string => {
            if (!submission.pcoPlus) {
                return ''
            }

            const toLocalTime = (date: Date): string => {
                return moment.tz(date, 'Europe/Berlin').format('DD.MM.YYYY HH:mm');
            };

            const startDate = submission.pcoPlus?.requestedDateTimePeriod?.startDate;
            const endDate = submission.pcoPlus?.requestedDateTimePeriod?.endDate;
            const dateRange = startDate && endDate ? `${toLocalTime(startDate)} - ${toLocalTime(endDate)}` : '–';

            return crlf +
                intl.formatMessage({id: 'submission.mailto.label.reuestedDateTime'}) + crlf +
                intl.formatMessage({id: 'submission.mailto.label.todate'}) + dateRange + crlf +
                intl.formatMessage({id: 'submission.mailto.label.new'}) + crlf;
        };

        // Änderungswünsche zu den Mitwirkenden der Einreichung
        const otherPersons = (): string => {
            const widget = submission.callForPapers.form.widgets.find(
                widget => widget.type === 'other_persons'
            );

            if (!widget || !widget.active) {
                return '';
            }

            const personList = submission.otherPersons?.map(item => {
                const roleName = _.get(item, `role.label.${intl.locale}`, '');

                return (item.person.mainContact.title ? `${item.person.mainContact.title} ` : '') +
                    (item.person.mainContact.firstName ? `${item.person.mainContact.firstName} ` : '') +
                    (item.person.mainContact.lastName ? `${item.person.mainContact.lastName} ` : '') +
                    (roleName ? `(${roleName})` : '');
            });

            return crlf +
                intl.formatMessage({id: 'submission.mailto.label.persons'}) + crlf +
                intl.formatMessage({id: 'submission.mailto.label.todate'}) +
                (personList?.length ? personList.join(', ') : '–') + crlf +
                intl.formatMessage({id: 'submission.mailto.label.new'}) + crlf;
        };

        // Zusammensetzen des Mail-Bodys
        const body = encodeURIComponent(
            preamble +
            requestedDateTimePeriod() +
            title +
            subtitle +
            abstract +
            otherPersons() +
            miscellaneous +
            crlf +
            farewell
        );

        setMailtoLink(`mailto:${recipient}?subject=${subject}&body=${body}`);

    }, [intl, submission]);

    // Haupt-Render der Functional Component
    return (
        <FormattedMessage id={intlId} values={{
            a: (chunks: string) => <a className="editDisabledHint" href={mailtoLink}>{chunks}</a>
        }}/>
    );
};

export default CallForPapersMailtoLink;
